// Why the payer is contacting support, derived from what the screen already
// knows rather than asked. A category the payer picks is a category the payer
// picks wrong, and the ticket then routes wrong.
export type SupportContext =
  | "CUSTOMER_NOT_FOUND"
  | "PRICE_UNAVAILABLE"
  | "PAYLINK_UNRESOLVED"
  | "PAYMENT_FAILED"
  | "PAYMENT_PENDING"
  | "RECEIPT_UNAVAILABLE"
  | "UNKNOWN";

// The journey stage the payer was in when it failed. Deliberately not the
// integer `currentStep` from paymentContext: that is the defect
// payment-journey-step-integrity.md exists about, and naming the stage here
// keeps this module readable without inheriting it.
export type SupportStage =
  | "search"
  | "price"
  | "pay"
  | "receipt"
  | "paylink"
  | "unknown";

export type SupportSignals = {
  stage?: SupportStage;
  // The catalogued code from the failure, when the screen holds one.
  code?: string;
  // Whether a payment was accepted but has not settled. A pending payment is
  // not a failure and must not be reported as one -- the payer is asking
  // where their money is, which is a different conversation.
  paymentPending?: boolean;
};

// Codes that name their own context regardless of where the payer was. A code
// is unique across the catalogue, so it is the stronger signal when present;
// the stage answers only when nothing more specific does.
const CONTEXT_BY_CODE: Record<string, SupportContext> = {
  "CPS-404-001": "CUSTOMER_NOT_FOUND",
};

const CONTEXT_BY_STAGE: Record<SupportStage, SupportContext> = {
  search: "CUSTOMER_NOT_FOUND",
  price: "PRICE_UNAVAILABLE",
  pay: "PAYMENT_FAILED",
  receipt: "RECEIPT_UNAVAILABLE",
  paylink: "PAYLINK_UNRESOLVED",
  unknown: "UNKNOWN",
};

export const getSupportContext = (signals: SupportSignals = {}): SupportContext => {
  // Checked before anything else: a pending payment reaching the failure
  // branches would tell the payer their payment failed while it is still in
  // flight, which is the one thing a payment surface must never say.
  if (signals.paymentPending) return "PAYMENT_PENDING";

  const byCode = signals.code ? CONTEXT_BY_CODE[signals.code] : undefined;
  if (byCode) return byCode;

  return CONTEXT_BY_STAGE[signals.stage ?? "unknown"];
};

export default getSupportContext;
